import { cn } from "@/lib/utils";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { useTranslation } from "react-i18next";

import {
  AdvancedSearchConfig,
  AdvancedSearchFieldConfig,
  extractLabel,
  findFieldByKey,
  filterOutForeignFields,
} from "@/config/forms/advanced-search/advanced-search-fields-def";
import { ChevronsUpDownIcon } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../ui/command";
import { CheckIcon } from "@radix-ui/react-icons";
import { useCallback, useMemo } from "react";

export const AdvancedQueryFieldSelector = ({
  value,
  className,
  onSelect,
  config,
  allowForeign,
}: {
  value?: AdvancedSearchFieldConfig;
  onSelect: (value: AdvancedSearchFieldConfig) => void;
  config: AdvancedSearchConfig;
  className?: string;
  allowForeign?: boolean; // allow a field that reference a foreign table
}) => {
  const { t } = useTranslation();

  const cleanConfig = useMemo(
    () => (allowForeign === false ? filterOutForeignFields(config) : config),
    [config, allowForeign],
  );

  const renderCommandItem = useCallback(
    (field: AdvancedSearchFieldConfig) => {
      return (
        <CommandItem
          key={field.key}
          value={t(extractLabel(field))}
          onSelect={() => {
            const relatedConfig = findFieldByKey(field.key, cleanConfig);

            if (!relatedConfig) return;

            onSelect(relatedConfig);
          }}
        >
          {t(extractLabel(field))}
          <CheckIcon
            className={cn(
              "ml-auto",
              field.key === value?.key ? "opacity-100" : "opacity-0",
            )}
          />
        </CommandItem>
      );
    },
    [cleanConfig, onSelect, value?.key, t],
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          className={cn(
            "w-1/2 min-h-fit justify-between !whitespace-wrap text-left",
            className,
          )}
        >
          {value != undefined && value.key
            ? t(extractLabel(value))
            : t("selectField")}
          <ChevronsUpDownIcon className="opacity-50 h-3 mr-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-fit p-0">
        <Command
          filter={(filterValue, search) =>
            filterValue
              ?.toLocaleLowerCase()
              ?.includes(search.toLocaleLowerCase())
              ? 1
              : 0
          }
        >
          <CommandInput placeholder={t("selectField")} className="h-9" />
          <CommandList>
            <CommandEmpty>{t("rechercheVideRechercheAvancee")}</CommandEmpty>
            {cleanConfig.map((o) =>
              o.type === "group" ? (
                <CommandGroup key={o.label} heading={t(o.label)}>
                  {o.fields.map((ofield) => renderCommandItem(ofield))}
                </CommandGroup>
              ) : (
                renderCommandItem(o)
              ),
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
